import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../store/auth';
import { signOut } from '../auth/cognito';

/**
 * If a Twilio token refresh has terminally failed and the active call (if
 * any) has ended, useDevice sets `reauthRequired`. Both guards observe it
 * and force a sign-out + redirect to /login. The flag is set only after
 * the in-flight call ends, so we never disconnect mid-conversation.
 */
function useEnforceReauth(): boolean {
  const reauthRequired = useAuth(s => s.reauthRequired);
  const clear = useAuth(s => s.clear);
  useEffect(() => {
    if (reauthRequired) {
      signOut();
      clear();
    }
  }, [reauthRequired, clear]);
  return reauthRequired;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  const reauth = useEnforceReauth();
  if (!ready) return <FullPageSpinner />;
  if (!user || reauth) return <Navigate to="/login" state={{ from: location }} replace />;
  return <>{children}</>;
}

export function RequireAdmin({ children }: { children: ReactNode }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  const reauth = useEnforceReauth();
  if (!ready) return <FullPageSpinner />;
  if (!user || reauth) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  return <>{children}</>;
}

function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
      Loading…
    </div>
  );
}
